import React from "react";
import ReactDOM from "react-dom";

import Employee from "app/domain/Employee";
import { showErrorMessage } from "usecases/commons/show-error-message";
import { findEmployeeFilter } from "infrastructure/employees-filter";
import { findEmployeesWithFields } from "infrastructure/employees-cache";
import { findCompanyId } from "infrastructure/repository/company-repository";
import { findModels } from "infrastructure/repository/benefit-models-repository";
import { translateEmployeeProperties } from "infrastructure/translate/translateEmployeeProperties";
import { logError, addContextLog } from "infrastructure/log";
import { translate } from "infrastructure/translate/localization";

import { findSelectedEmployees, selectAllEmployees } from "../select-employees";
import ActiveEmployees from "../components/ActiveEmployees";
import { displayEmployeeFilters } from "./display-employees-filters";

class ActiveEmployee {
  constructor({
    id,
    internalId,
    costCenter,
    payrollCompany,
    nameAndSurnames,
    benefitModelName,
    cardState,
    state,
    inMigration,
  }) {
    this.id = id;
    this.internalId = internalId;
    this.costCenter = costCenter;
    this.payrollCompany = payrollCompany;
    this.nameAndSurnames = nameAndSurnames;
    this.benefitModelName = benefitModelName;
    this.cardState = cardState;
    this.state = state;
    this.inMigration = inMigration;
  }
}

export function renderActiveEmployees({
  employees,
  currentEmployees,
  employeeIds,
  selectedIds,
  models,
  currentPage,
  pageCount,
  employeeCount,
  filters,
}) {
  const results = employees.map((e) => {
    return new ActiveEmployee({
      id: e.id,
      internalId: e.internalId || "-",
      costCenter: e.costCenter || "-",
      payrollCompany: e.payrollCompany || "-",
      nameAndSurnames: `${e.name} ${e.surname}`,
      benefitModelName: models.find((m) => m.id === e.modelId)?.name,
      cardState: "fake-card-state",
      state: e.state,
      inMigration: e.inMigration,
    });
  });

  const taxRegimesFormatted = Employee.taxRegimes.map((tax) => ({
    id: tax,
    name: translateEmployeeProperties("taxRegime", tax),
  }));

  const target = document.getElementById("table-active-employees-container");

  if (target) {
    ReactDOM.render(
      <ActiveEmployees
        employees={results}
        currentEmployees={currentEmployees}
        currentPage={currentPage}
        employeeIds={employeeIds}
        pageCount={pageCount}
        employeeCount={employeeCount}
        selected={selectedIds}
        benefitModels={models}
        taxRegimes={taxRegimesFormatted}
        currentFilters={filters}
      />,
      target
    );
  }
}

export async function displayActiveEmployees({
  pageSize = 15,
  currentPage = 0,
  employeeFilter = () => true,
  selectAll = false,
} = {}) {
  try {
    const keyToSearch = ["internalId", "name", "surname", "legalId", "email"];
    const companyId = await findCompanyId();
    const allEmployees = await findEmployeesWithFields({ companyId });
    const currentEmployees = allEmployees
      .filter((e) => e.state !== "uploading")
      .filter(findEmployeeFilter({ forState: "active", keys: keyToSearch }))
      .filter(employeeFilter);

    const employeeCount = currentEmployees.length;

    const pageCount = Math.ceil(employeeCount / pageSize);

    let pageIndex = currentPage;

    if (pageIndex > pageCount - 1) pageIndex = pageCount - 1;
    if (pageIndex < 0) pageIndex = 0;

    const employees = currentEmployees.slice(
      pageIndex * pageSize,
      (pageIndex + 1) * pageSize
    );
    const { data: models } = await findModels();

    const employeeIds = currentEmployees.map((e) => e.id);
    if (selectAll) {
      selectAllEmployees({ ids: employeeIds });
      displayEmployeeFilters({ ids: employeeIds });
    }

    const selectedIds = findSelectedEmployees({ of: employeeIds });

    addContextLog("info", "displayActiveEmployees", "display-screen", {
      companyId,
      employeeIds,
    });
    renderActiveEmployees({
      companyId,
      employees,
      currentEmployees,
      employeeIds,
      selectedIds,
      models,
      currentPage: pageIndex,
      pageCount,
      filters: employeeFilter,
    });
  } catch (e) {
    logError(e, `Error to displayActiveEmployees in page ${currentPage}`);
    showErrorMessage(translate("common-messages.basic-error-message"), true, e);
  }
}

export const getEmployeesCurrentPage = () => {
  const page = localStorage.getItem("employees-list-page");
  if (page && page !== "") {
    return parseInt(page, 10);
  }
  localStorage.setItem("employees-list-page", 0);

  return 0;
};

export const setEmployeesCurrentPage = (page) =>
  localStorage.setItem("employees-list-page", page);
